/**
 * `/` opens the saved-prompt picker — the detector, pure (2026-09-17).
 *
 * IT DOES NOT SHARE `detectActiveMention`, AND THE DIFFERENCE IS THE DESIGN.
 * That scanner (services/threadMentions/mentionParser.ts) opens after ANY
 * whitespace and stops at the next one, which is exactly right for `@handle`
 * and exactly wrong for `/`:
 *
 *   · opening after any whitespace would pop a picker inside `and/or`, `24/7`,
 *     `9/17/2026`, `https://example.com` and `src/components/x.ts` — every one
 *     of which a person types mid-sentence without meaning anything by it;
 *   · stopping at whitespace would cap the query at one word, and prompt titles
 *     have spaces ("new matter intake").
 *
 * So this one opens ONLY at the very start of the draft or immediately after a
 * newline — the two places a line begins, which is where a command-style `/`
 * belongs and nowhere else — and its query may contain spaces up to a bound.
 * A sibling with deliberately different rules, with the reason written down, is
 * the `MOBILE_VIEWPORT_MAX_PX` precedent.
 *
 * Pure, and takes the text and the caret rather than a DOM node, so every rule
 * above can be asserted directly instead of through a rendered composer.
 */

export interface ActivePromptSlash {
  /** What was typed after the `/`. May contain spaces; may be empty. */
  query: string;
  /** Index of the `/` itself. */
  start: number;
  /** The caret — the end of the span this replaces. */
  end: number;
}

/**
 * How much may be typed after `/` before the picker gives up.
 *
 * Past this, the person is writing a message that happens to begin with a
 * slash, not searching for a prompt: no saved-prompt NAME is 40 characters of
 * search text, and a picker still open at that point is a panel sitting over
 * what they are trying to write.
 */
export const SLASH_QUERY_MAX = 40;

/**
 * The active `/query`, or null.
 *
 * `null` is the common answer and the safe one: anything this is not certain
 * about leaves the composer alone, because a picker that opens uninvited
 * interrupts typing, and one that fails to open costs a click on the icon that
 * is already there.
 */
export function detectPromptSlash(text: string, caret: number): ActivePromptSlash | null {
  if (typeof text !== 'string' || !text) return null;
  const pos = Math.max(0, Math.min(caret, text.length));

  // Walk left from the caret to the `/` that opened this, refusing to cross a
  // line break: a slash on the line above is not this line's command.
  let i = pos;
  while (i > 0) {
    const ch = text[i - 1];
    if (ch === '\n') return null;
    if (ch === '/') {
      const before = i - 2 >= 0 ? text[i - 2] : '';
      // THE WHOLE RULE: a line must BEGIN with it. Start of the draft, or the
      // first character after a newline — never "after a space", which is what
      // makes `and/or` and `https://…` innocent.
      if (before !== '' && before !== '\n') return null;
      const query = text.slice(i, pos);
      // `//` closes it: the second slash is how somebody says they meant a
      // literal one, and it costs them one keystroke rather than an Escape.
      if (query.startsWith('/')) return null;
      if (query.length > SLASH_QUERY_MAX) return null;
      return { query, start: i - 1, end: pos };
    }
    i -= 1;
  }
  return null;
}

/**
 * The draft with the `/query` span replaced by the prompt's own text.
 *
 * Returns the WHOLE new draft rather than a fragment, because the composer is
 * an uncontrolled contentEditable: insertion there goes through
 * `execCommand('insertText')` over a selection, and computing a full
 * replacement lets the caller reuse the select-all-then-insert path the picker
 * already uses instead of doing Range arithmetic across nested nodes (the
 * 2026-08-08 desync).
 */
export function replaceSlashSpan(
  text: string,
  active: Pick<ActivePromptSlash, 'start' | 'end'>,
  replacement: string,
): string {
  const start = Math.max(0, Math.min(active.start, text.length));
  const end = Math.max(start, Math.min(active.end, text.length));
  return text.slice(0, start) + replacement + text.slice(end);
}
